import React, { useState, useEffect, useRef } from "react";
import {
  collection,
  addDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  serverTimestamp,
  limit,
  getDocs,
  startAfter,
  disableNetwork,
  enableNetwork,
  updateDoc,
  doc,
  writeBatch,
} from "firebase/firestore";
import { db } from "@/lib/firebase"; // Make sure you have this firebase config
import { useAuth } from "@/context/AuthContext";
import { Card, CardHeader, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Send, Search } from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { ThreeDots } from "react-loader-spinner";
import { useNavigate } from 'react-router-dom'; // Change this line

const MESSAGES_PER_PAGE = 20;

const MessagingSystem = () => {
  const navigate = useNavigate(); // Change this line
  const { user } = useAuth();
  const [selectedUser, setSelectedUser] = useState(null);
  const [message, setMessage] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [messages, setMessages] = useState([]);
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const messagesEndRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [lastMessage, setLastMessage] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [unreadCount, setUnreadCount] = useState({});

  // Handle offline/online status
  useEffect(() => {
    const handleOnline = () => {
      setIsOffline(false);
      enableNetwork(db);
    };

    const handleOffline = () => {
      setIsOffline(true);
      disableNetwork(db);
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  // Fetch all users except current user
  useEffect(() => {
    if (!user) return;

    try {
      setLoading(true);
      const usersRef = collection(db, "users");
      const unsubscribe = onSnapshot(usersRef, (snapshot) => {
        const usersList = snapshot.docs
          .map((doc) => ({
            id: doc.id,
            ...doc.data(),
            isOnline: doc.data().lastSeen
              ? Date.now() - doc.data().lastSeen.toDate() < 300000
              : false,
          }))
          .filter((u) => u.id !== user?.uid);

        setUsers(usersList);
        setFilteredUsers(usersList);
        setLoading(false);
      });

      return () => unsubscribe();
    } catch (error) {
      console.error("Error fetching users:", error);
      setLoading(false);
    }
  }, [user]);

  // Load initial messages with pagination
  const loadMessages = async (chatId) => {
    try {
      const messagesRef = collection(db, "messages");
      const q = query(
        messagesRef,
        where("chatId", "==", chatId),
        orderBy("timestamp", "desc"),
        limit(MESSAGES_PER_PAGE)
      );

      const snapshot = await getDocs(q);
      const msgs = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        timestamp:
          doc.data().timestamp?.toDate()?.toISOString() ||
          new Date().toISOString(),
      }));

      setMessages(msgs.reverse());
      setLastMessage(snapshot.docs[0]);
      setHasMore(snapshot.docs.length === MESSAGES_PER_PAGE);
    } catch (error) {
      console.error("Error loading messages:", error);
    }
  };

  // Load more messages
  const loadMoreMessages = async () => {
    if (!lastMessage || !selectedUser || !hasMore) return;

    try {
      const chatId = [user.uid, selectedUser.id].sort().join("_");
      const messagesRef = collection(db, "messages");
      const q = query(
        messagesRef,
        where("chatId", "==", chatId),
        orderBy("timestamp", "desc"),
        startAfter(lastMessage),
        limit(MESSAGES_PER_PAGE)
      );

      const snapshot = await getDocs(q);
      const newMsgs = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        timestamp:
          doc.data().timestamp?.toDate()?.toISOString() ||
          new Date().toISOString(),
      }));

      setMessages((prev) => [...newMsgs.reverse(), ...prev]);
      setLastMessage(snapshot.docs[0]);
      setHasMore(snapshot.docs.length === MESSAGES_PER_PAGE);
    } catch (error) {
      console.error("Error loading more messages:", error);
    }
  };

  // Clear messages when a new user is selected
  useEffect(() => {
    setMessages([]);
    setLastMessage(null);
    setHasMore(true);
  }, [selectedUser]);

  // Modified message fetching with read receipts
  useEffect(() => {
    if (!selectedUser || !user) return;

    const chatId = [user.uid, selectedUser.id].sort().join("_");

    // Mark messages as read when chat is opened
    const markMessagesAsRead = async (messages) => {
      const batch = writeBatch(db);
      messages.forEach((msg) => {
        if (msg.receiverId === user.uid && !msg.read) {
          const msgRef = doc(db, "messages", msg.id);
          batch.update(msgRef, { read: true });
        }
      });
      await batch.commit();
    };

    // Load and subscribe to messages
    const loadAndSubscribeMessages = () => {
      const messagesRef = collection(db, "messages");
      const q = query(
        messagesRef,
        where("chatId", "==", chatId),
        orderBy("timestamp", "desc"),
        limit(MESSAGES_PER_PAGE)
      );

      return onSnapshot(q, (snapshot) => {
        const msgs = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          timestamp:
            doc.data().timestamp?.toDate()?.toISOString() ||
            new Date().toISOString(),
        }));

        setMessages(msgs.reverse());
        setLastMessage(snapshot.docs[0]);
        setHasMore(snapshot.docs.length === MESSAGES_PER_PAGE);

        // Mark messages as read
        markMessagesAsRead(msgs);
      });
    };

    const unsubscribe = loadAndSubscribeMessages();
    return () => unsubscribe();
  }, [selectedUser, user]);

  // Track unread messages for all conversations
  useEffect(() => {
    if (!user) return;

    const unreadQuery = query(
      collection(db, "messages"),
      where("receiverId", "==", user.uid),
      where("read", "==", false)
    );

    const unsubscribe = onSnapshot(unreadQuery, (snapshot) => {
      const counts = {};
      snapshot.docs.forEach((doc) => {
        const senderId = doc.data().senderId;
        counts[senderId] = (counts[senderId] || 0) + 1;
      });
      setUnreadCount(counts);
    });

    return () => unsubscribe();
  }, [user]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Modified send message function
  const sendMessage = async (e) => {
    e.preventDefault();
    if (!message.trim() || !selectedUser || !user) return;

    const chatId = [user.uid, selectedUser.id].sort().join("_");
    const newMessage = {
      chatId,
      text: message.trim(),
      senderId: user.uid,
      receiverId: selectedUser.id,
      senderName: user.displayName || user.email,
      receiverName: selectedUser.name,
      timestamp: serverTimestamp(),
      read: false,
      delivered: false,
      senderPhotoURL: user.photoURL || null,
      receiverPhotoURL: selectedUser.profilePic || null,
    };

    try {
      // Optimistic update
      const optimisticMsg = {
        ...newMessage,
        id: Date.now().toString(),
        timestamp: new Date().toISOString(),
        pending: true,
      };

      setMessages((prev) => [...prev, optimisticMsg]);
      setMessage("");
      scrollToBottom();

      // Add to Firestore
      const docRef = await addDoc(collection(db, "messages"), newMessage);

      // Update delivered status
      await updateDoc(doc(db, "messages", docRef.id), {
        delivered: true,
      });
    } catch (error) {
      console.error("Error sending message:", error);
      setMessages((prev) =>
        prev.filter((msg) => msg.id !== Date.now().toString())
      );
      setMessage(newMessage.text);
    }
  };

  const handleProfileClick = (userId, e) => {
    e.stopPropagation(); // Prevent triggering chat selection
    navigate(`/profile/${userId}`); // Change this line
  };

  const renderMessage = (msg) => (
    <div
      key={msg.id}
      className={`flex items-start gap-3 ${
        msg.senderId === user?.uid ? "flex-row-reverse" : "flex-row"
      }`}
    >
      <Avatar 
        className="h-8 w-8 mt-1 cursor-pointer hover:opacity-80 transition-opacity"
        onClick={(e) => handleProfileClick(msg.senderId, e)}
      >
        <AvatarImage
          src={
            msg.senderId === user?.uid ? user?.photoURL : selectedUser?.profilePic
          }
          alt={msg.senderId === user?.uid ? "You" : selectedUser?.name}
        />
        <AvatarFallback className="bg-blue-500 text-white text-sm">
          {msg.senderId === user?.uid
            ? user?.displayName?.[0]
            : selectedUser?.name?.[0]}
        </AvatarFallback>
      </Avatar>
      <div
        className={`max-w-[70%] rounded-lg p-3 ${
          msg.senderId === user?.uid
            ? "bg-blue-500 text-white"
            : "bg-gray-100 dark:bg-gray-700 dark:text-white"
        }`}
      >
        <p>{msg.text}</p>
        <p className="text-xs mt-1 opacity-70">
          {new Date(msg.timestamp).toLocaleTimeString()}
        </p>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <ThreeDots height="50" width="50" color="#3b82f6" ariaLabel="loading" />
      </div>
    );
  }

  // Add load more button to UI
  const renderMessages = () => (
    <div className="flex-1 overflow-y-auto p-responsive custom-scrollbar">
      {hasMore && (
        <div className="flex justify-center">
          <button
            onClick={loadMoreMessages}
            className="px-4 py-1 text-sm text-sky-500 hover:bg-sky-100 hover:text-black dark:text-sky-400 dark:hover:bg-sky-800 rounded-full"
          >
            Load more messages
          </button>
        </div>
      )}
      {messages.map(renderMessage)}
      <div ref={messagesEndRef} />
    </div>
  );

  // Modify user list to show unread count
  const renderUserList = () => (
    <div className="overflow-y-auto h-[calc(100%-120px)]">
      {filteredUsers.map((user) => (
        <div
          key={user.id}
          onClick={() => setSelectedUser(user)}
          className={`p-4 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer ${
            selectedUser?.id === user.id ? "bg-gray-100 dark:bg-gray-700" : ""
          }`}
        >
          <div className="flex items-center gap-3">
            <div className="relative">
              <Avatar 
                className="h-10 w-10 cursor-pointer hover:opacity-80 transition-opacity"
                onClick={(e) => handleProfileClick(user.id, e)}
              >
                <AvatarImage src={user.profilePic} alt={user.name} />
                <AvatarFallback className="bg-blue-500 text-white">
                  {user.name.charAt(0)}
                </AvatarFallback>
              </Avatar>
              {unreadCount[user.id] > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {unreadCount[user.id]}
                </span>
              )}
            </div>
            <div className="flex-1">
              <h3 className="font-medium dark:text-white">{user.name}</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Batch of {user.batch}
              </p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="container-pad min-h-screen-75">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 h-[calc(100vh-5rem)]">
        {/* Contacts List */}
        <div className="lg:col-span-4 card-base overflow-hidden order-2 lg:order-1 min-h-[400px] lg:min-h-0">
          <div className="p-4 border-b dark:border-gray-700">
            <h2 className="text-lg font-semibold dark:text-white">Messages</h2>
            <div className="relative mt-4">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                className="pl-10 dark:bg-gray-700 dark:text-gray-100"
                placeholder="Search alumni by name or batch..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          {renderUserList()}
        </div>

        {/* Chat Area */}
        <div className="lg:col-span-8 card-base flex flex-col order-1 lg:order-2 min-h-[500px] lg:min-h-0">
          {selectedUser ? (
            <>
              <div className="p-4 border-b dark:border-gray-700">
                <div className="flex items-center gap-3">
                  <Avatar className="h-10 w-10">
                    <AvatarImage
                      src={selectedUser.profilePic}
                      alt={selectedUser.name}
                    />
                    <AvatarFallback className="bg-blue-500 text-white">
                      {selectedUser.name.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <h3 className="font-medium dark:text-white">
                      {selectedUser.name}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Batch of {selectedUser.batch}
                    </p>
                  </div>
                </div>
              </div>

              {renderMessages()}

              <form
                onSubmit={sendMessage}
                className="p-4 border-t dark:border-gray-700"
              >
                <div className="flex space-x-2">
                  <Input
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Type a message..."
                    className="dark:bg-gray-700 dark:text-gray-100"
                  />
                  <Button type="submit" disabled={!message.trim()}>
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </form>
            </>
          ) : (
            <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
              Select a user to start messaging
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MessagingSystem;
